import Icon from '../../../components/AppIcon';
import type { WorkflowStep } from '../types';

const WorkflowSection = () => {
  const workflowSteps: WorkflowStep[] = [
    {
      id: "1",
      title: "Customer Inquiry",
      description: "Customer initiates conversation through any channel - website, WhatsApp, or social media",
      icon: "MessageCircle",
      color: "bg-blue-500"
    },
    {
      id: "2",
      title: "AI Processing",
      description: "OpenAI GPT-4 analyzes the query, understands context, and determines the best response",
      icon: "Brain",
      color: "bg-purple-500"
    },
    {
      id: "3",
      title: "Data Retrieval",
      description: "Bot accesses your knowledge base, CRM, or database to gather relevant information",
      icon: "Database",
      color: "bg-green-500"
    },
    {
      id: "4",
      title: "Intelligent Response",
      description: "AI generates personalized response and takes action (create ticket, update order, etc.)",
      icon: "Sparkles",
      color: "bg-amber-500"
    },
    {
      id: "5",
      title: "Human Handoff",
      description: "If needed, seamlessly transfers to human agent with full conversation context",
      icon: "Users",
      color: "bg-red-500"
    },
    {
      id: "6",
      title: "Continuous Learning",
      description: "System learns from interactions to improve future responses and accuracy",
      icon: "TrendingUp",
      color: "bg-indigo-500"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Workflow" size={16} />
            <span>How It Works</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Intelligent Automation Workflow
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            See how our AI chatbot processes customer inquiries from start to finish
          </p>
        </div>
        
        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary transform -translate-y-1/2"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
            {workflowSteps.map((step, index) => (
              <div key={step.id} className="relative">
                <div className="bg-card rounded-xl p-6 border border-border hover:shadow-lg transition-all duration-fast hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg ${step.color} flex items-center justify-center`}>
                      <Icon name={step.icon} size={24} className="text-white" />
                    </div>
                    <span className="text-2xl font-bold text-text-tertiary/20">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    {step.title}
                  </h3>
                  
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {step.description}
                  </p>
                </div>
                
                {index < workflowSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <Icon name="ArrowRight" size={24} className="text-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium">
            <Icon name="Zap" size={16} />
            <span>Average processing time: &lt;2 seconds</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;